import React, { useEffect, useState } from 'react';
import { Modal, NumberInput, SegmentedControl } from '@mantine/core';
import { resolveEffectiveMinimumOrder } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import { formatMoneyCompact, MONEY_DECIMALS } from '../../../../../utils/money';
import classes from './set-minimum-order-modal.module.scss';

export type MinimumOrderPayload = { minimumOrder: number; minimumOrderPerDiner: number };

export type SetMinimumOrderModalProps = {
  opened: boolean;
  tableNumber: string;
  dinersCount: number;
  initialMinimumOrder: number;
  initialMinimumOrderPerDiner: number;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: MinimumOrderPayload) => void;
};

export const SetMinimumOrderModal: React.FC<SetMinimumOrderModalProps> = ({
  opened,
  tableNumber,
  dinersCount,
  initialMinimumOrder,
  initialMinimumOrderPerDiner,
  isSaving,
  onClose,
  onSave,
}) => {
  const [mode, setMode] = useState<'table' | 'diner'>('table');
  const [amount, setAmount] = useState<number | string>('');

  useEffect(() => {
    if (!opened) return;
    const perDiner = initialMinimumOrderPerDiner > 0;
    setMode(perDiner ? 'diner' : 'table');
    const initial = perDiner ? initialMinimumOrderPerDiner : initialMinimumOrder;
    setAmount(initial > 0 ? initial : '');
  }, [opened, initialMinimumOrder, initialMinimumOrderPerDiner]);

  const numericAmount = Number(amount) || 0;
  const diners = dinersCount > 0 ? dinersCount : 1;

  const handleModeChange = (next: string) => {
    const nextMode = next === 'diner' ? 'diner' : 'table';
    setMode(nextMode);
    const initial = nextMode === 'diner' ? initialMinimumOrderPerDiner : initialMinimumOrder;
    setAmount(initial > 0 ? initial : '');
  };

  const handleSave = () => {
    onSave(mode === 'diner'
      ? { minimumOrder: 0, minimumOrderPerDiner: numericAmount }
      : { minimumOrder: numericAmount, minimumOrderPerDiner: 0 });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<strong>{`מינימום הזמנה — שולחן ${tableNumber}`}</strong>}
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: navyModal.root }}
      zIndex={420}
      closeOnClickOutside={false}
      closeButtonProps={{ 'data-testid': 'set-minimum-order-close' } as Record<string, string>}
    >
      <div className={classes.stack}>
        <SegmentedControl
          fullWidth
          value={mode}
          onChange={handleModeChange}
          data-testid="set-minimum-order-mode"
          data={[
            { label: 'לשולחן', value: 'table' },
            { label: 'לסועד', value: 'diner' },
          ]}
        />

        <NumberInput
          data-testid="set-minimum-order-amount"
          label={mode === 'diner' ? 'סכום לסועד (₪)' : 'סכום לשולחן (₪)'}
          value={amount}
          onChange={setAmount}
          placeholder="0"
          min={0}
          max={100000}
          step={10}
          decimalScale={MONEY_DECIMALS}
          size="md"
          radius="md"
        />

        {mode === 'diner' && numericAmount > 0 && (
          <p className={classes.math} data-testid="set-minimum-order-math">
            {`${diners} סועדים × ${formatMoneyCompact(numericAmount)} ₪ = מינימום ${formatMoneyCompact(resolveEffectiveMinimumOrder({ minimumOrder: 0, minimumOrderPerDiner: numericAmount }, diners))} ₪`}
          </p>
        )}

        <p className={classes.hint}>0 מבטל את המינימום לשולחן זה</p>

        <div className={classes.actions}>
          <Button variant="cancel" onClick={onClose} disabled={isSaving} dataTestId="set-minimum-order-cancel">
            ביטול
          </Button>
          <Button
            variant="save"
            onClick={handleSave}
            disabled={isSaving}
            loading={isSaving}
            loadingLabel="שומר..."
            dataTestId="set-minimum-order-save"
          >
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};
